import { STATUS_CONFIG, WORKFLOW_COLUMNS } from './ServiceLogsConfig';
import { useConfig } from '../../../contexts/ConfigContext';
import { useToast } from '../../../components/Toast';
import type { InvoiceSends, SendRecord } from './serviceLogsSendHistory';

const SEND_TONE: Record<SendRecord['state'], string> = {
  sent: 'chip chip--primary',
  opened: 'chip chip--success',
  failed: 'chip chip--danger',
};

/** "Invoice emailed 20/10/2026, 16:27, opened", or "Not emailed". */
function sendTitle(channel: 'email' | 'whatsApp', send: SendRecord | undefined, formatDateTime: (d: string) => string): string {
  if (!send) return channel === 'email' ? 'Not emailed' : 'Not sent by WhatsApp';
  const what = send.kind === 'reminder' ? 'Reminder' : 'Invoice';
  const how = channel === 'email' ? 'emailed' : 'sent by WhatsApp';
  const state = send.state === 'opened'
    ? (channel === 'email' ? ', opened' : ', read')
    : send.state === 'failed'
      ? (channel === 'email' ? ', bounced' : ', failed')
      : '';
  const reason = send.reason ? `. ${send.reason}` : '';
  return `${what} ${how} ${formatDateTime(send.at)}${state}${reason}`;
}

interface BoardServiceLog {
  id: string;
  status: string;
  customer_id?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  total_amount?: number;
  service_date: string;
  territory_name?: string;
  operator_name?: string;
  invoice_number?: string;
  invoice_id?: string;
  internal_notes?: string;
  additional_cc_emails?: string;
  [key: string]: unknown;
}

interface BoardViewProps {
  logsByStatus: Record<string, BoardServiceLog[]>;
  columnSorts: Record<string, string>;
  onColumnSortChange: (colId: string, sortType: string) => void;
  onView: (log: BoardServiceLog) => void;
  onEdit: (log: BoardServiceLog, e?: React.MouseEvent) => void;
  onCreateInvoice: (log: BoardServiceLog, e?: React.MouseEvent) => void;
  onSendInvoice: (invoiceId: string, additionalCcEmails?: string) => void;
  onWhatsApp: (log: BoardServiceLog, e?: React.MouseEvent) => void;
  onFollowUp: (log: BoardServiceLog, e?: React.MouseEvent) => void;
  onUnlinkDraft: (log: BoardServiceLog) => void;
  canEdit: boolean;
  canApprove: boolean;
  canSend: boolean;
  canWhatsApp: boolean;
  canUnlinkDraft: boolean;
  sendingFollowUpFor: string | null;
  creatingInvoiceFor: string | null;
  formatCurrency: (amount: number) => string;
  formatDate: (dateStr: string) => string;
  bouncingByCustomerId?: Map<string, { lastBounceAt: string | null }>;
  bouncingEmails?: Set<string>;
  /** Email and WhatsApp sends per invoice number. Left out for people who cannot send. */
  sendHistory?: Map<string, InvoiceSends>;
}

export default function BoardView({
  logsByStatus, columnSorts, onColumnSortChange, onView, onEdit, onCreateInvoice, onSendInvoice, onWhatsApp, onFollowUp: _onFollowUp, onUnlinkDraft, canEdit, canApprove,
  canSend, canWhatsApp, canUnlinkDraft, sendingFollowUpFor: _sendingFollowUpFor, creatingInvoiceFor, formatCurrency, formatDate, bouncingByCustomerId, bouncingEmails, sendHistory
}: BoardViewProps) {
  const { formatDateTime } = useConfig();
  const { showToast } = useToast();
  return (
    <div className="ops-board">
      {WORKFLOW_COLUMNS.map(col => {
        const logs = logsByStatus[col.id] || [];
        const status = STATUS_CONFIG[col.id] || STATUS_CONFIG['draft']!;
        const currentSort = columnSorts[col.id] || 'date_desc';

        return (
          <div key={col.id} className="ops-board-column">
            <div className="ops-board-column-header" style={{ borderTopColor: status.color }}>
              <div className="ops-board-column-title-group">
                <span className="ops-board-column-title">{col.label}</span>
                <span className="ops-board-column-count">{logs.length}</span>
              </div>
              <select
                className="ops-board-column-sort"
                value={currentSort}
                onChange={(e) => onColumnSortChange(col.id, e.target.value)}
              >
                <optgroup label="Sort">
                  <option value="date_desc">Newest</option>
                  <option value="date_asc">Oldest</option>
                  <option value="amount_desc">Amount ↓</option>
                  <option value="amount_asc">Amount ↑</option>
                  <option value="customer_asc">Customer A-Z</option>
                  <option value="customer_desc">Customer Z-A</option>
                  <option value="invoice_desc">Invoice # ↓</option>
                  <option value="invoice_asc">Invoice # ↑</option>
                </optgroup>
              </select>
            </div>
            <div className="ops-board-column-body">
              {logs.length === 0 ? (
                <div className="ops-board-empty">No items</div>
              ) : (
                logs.slice(0, 20).map((log: BoardServiceLog) => {
                  const isPending = log.status === 'pending';
                  const isDraft = log.status === 'draft';
                  const isSent = log.status === 'sent';
                  const isOverdue = log.status === 'overdue';
                  const isPaid = log.status === 'paid';

                  const showEdit = canEdit && (isPending || isDraft || isSent || isOverdue || isPaid || log.status === 'partial' || log.status === 'completed' || log.status === 'viewed');
                  const showApprove = canApprove && isPending;
                  const showSend = canSend && isDraft;
                  const showDeleteDraft = canUnlinkDraft && isDraft && log.invoice_id;
                  // Single Send Invoice / Send Reminder button — opens the
                  // share pop-up with email, WhatsApp, SMS, copy link.
                  const sends = sendHistory && (isSent || isOverdue) && log.invoice_number
                    ? sendHistory.get(log.invoice_number) ?? {}
                    : null;
                  const showSendAction = ((canSend || (canWhatsApp && log.customer_phone)))
                    && (isSent || isOverdue) && log.invoice_id && log.invoice_number;

                  const emailKey = log.customer_email?.toLowerCase() ?? '';
                  const isBouncing = (bouncingEmails?.has(emailKey) ?? false)
                    || (log.customer_id ? bouncingByCustomerId?.has(log.customer_id) ?? false : false);
                  const bounceInfo = log.customer_id ? bouncingByCustomerId?.get(log.customer_id) : undefined;
                  const bounceTooltip = isBouncing
                    ? (() => {
                        const last = bounceInfo?.lastBounceAt;
                        const days = last ? Math.floor((Date.now() - new Date(last).getTime()) / 86_400_000) : null;
                        const when = days === null ? '' : days <= 0 ? ' today' : days === 1 ? ' 1 day ago' : ` ${days} days ago`;
                        const addr = log.customer_email ? ` (${log.customer_email})` : '';
                        return `Customer's email${addr} last bounced${when}. Sending the invoice won't deliver until the address is fixed.`;
                      })()
                    : '';
                  // Territory shown bottom-right on the invoice line. Drop the
                  // "CleanMyBed" brand prefix (and a "CleanMyBed - " variant) so
                  // only the area name shows; the CSS truncates it to one line.
                  const territoryShort = log.territory_name
                    ? log.territory_name.replace(/^CleanMyBed\s*-?\s*/i, '').trim()
                    : '';
                  // Operator shown as "First L" (first name + surname initial).
                  // Strip any "CMB -" / "CleanMyBed" prefix first, and skip the
                  // initial when the trailing token isn't a real surname.
                  const operatorShort = (() => {
                    if (!log.operator_name) return '';
                    const cleaned = log.operator_name.replace(/^(CMB|CleanMyBed)\s*-?\s*/i, '').trim();
                    const parts = cleaned.split(/\s+/).filter(Boolean);
                    const first = parts[0] ?? cleaned;
                    if (parts.length <= 1) return first;
                    const initial = (parts[parts.length - 1] ?? '').charAt(0);
                    return /[a-z]/i.test(initial) ? `${first} ${initial.toUpperCase()}` : first;
                  })();
                  return (
                    <div key={log.id} className="ops-board-card" onClick={() => onView(log)}>
                      <div className="ops-board-card-header">
                        <span className="ops-board-card-customer">
                          {log.customer_name || 'Unknown'}
                          {log.internal_notes && <span title={log.internal_notes} className="chip chip--warning ml-xs">Note</span>}
                        </span>
                        {isBouncing && (
                          <span
                            title={bounceTooltip}
                            aria-label="Email bouncing"
                            style={{ fontSize: 'var(--text-base)', cursor: 'help', flexShrink: 0, marginLeft: '4px' }}
                          >
                            ⚠️
                          </span>
                        )}
                        <span className="ops-board-card-amount">{formatCurrency(log.total_amount || 0)}</span>
                      </div>
                      <div className="ops-board-card-meta">
                        <span>{formatDate(log.service_date)}</span>
                        {operatorShort && (
                          <span className="sl-meta-light ml-auto" title={log.operator_name}>{operatorShort}</span>
                        )}
                      </div>
                      {(log.invoice_number || territoryShort) && (
                        <div className="ops-board-card-footer">
                          <span className="ops-board-card-invoice">{log.invoice_number || ''}</span>
                          {sends && (
                            <span className="ops-board-card-sends">
                              {(['email', 'whatsApp'] as const).map(channel => {
                                const title = sendTitle(channel, sends[channel], formatDateTime);
                                return (
                                  <span
                                    key={channel}
                                    role="button"
                                    tabIndex={0}
                                    className={sends[channel] ? SEND_TONE[sends[channel]!.state] : 'chip'}
                                    title={title}
                                    aria-label={title}
                                    onClick={(e) => {
                                      // Tapping the icon says what went and why it failed, without opening the card.
                                      e.stopPropagation();
                                      showToast(title, sends[channel]?.state === 'failed' ? 'warning' : 'info');
                                    }}
                                  >
                                    {channel === 'email' ? '📩' : '💬'}
                                  </span>
                                );
                              })}
                            </span>
                          )}
                          {territoryShort && (
                            <span className="ops-board-card-territory" title={log.territory_name}>{territoryShort}</span>
                          )}
                        </div>
                      )}
                      {!isPaid && (
                        <div className="ops-board-card-actions" onClick={e => e.stopPropagation()}>
                          {showEdit && (
                            <button className="ops-board-action" onClick={(e) => onEdit(log, e)}>Edit</button>
                          )}
                          {showApprove && (
                            <button
                              className="ops-board-action ops-board-action-primary"
                              onClick={(e) => onCreateInvoice(log, e)}
                              disabled={creatingInvoiceFor === log.id}
                            >
                              {creatingInvoiceFor === log.id ? 'Creating...' : 'Approve'}
                            </button>
                          )}
                          {showSend && (
                            <button
                              className="ops-board-action ops-board-action-primary"
                              onClick={(e) => { e.stopPropagation(); onSendInvoice(log.invoice_id!, log.additional_cc_emails); }}
                            >
                              Send
                            </button>
                          )}
                          {showDeleteDraft && (
                            <button
                              className="ops-board-action sl-btn-danger-outline"
                              onClick={(e) => { e.stopPropagation(); onUnlinkDraft(log); }}
                              title="Remove draft invoice and return to pending"
                            >
                              Undo Approve
                            </button>
                          )}
                          {showSendAction && (
                            <button
                              className="ops-board-action ops-board-action-primary"
                              onClick={(e) => onWhatsApp(log, e)}
                              title="Choose how to send: Email, WhatsApp, SMS, or copy the link"
                            >
                              {isOverdue ? 'Send Reminder' : 'Send Invoice'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              {logs.length > 20 && (
                <div className="ops-board-more">+{logs.length - 20} more</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
